import { CSS_VERSIONS } from 'virtual:css-versions';

import { logger } from '../core/logger';
import { hideSkeleton } from './skeleton-loader';
import { environment } from '../environments/environment';
import { PaymentComponentConfig } from '../types/external/payment-component';
import { PaymentOutputEvent } from '../types/external/payment-output-event';
import { getCssVariablesList } from './styling-customization';
import {
  AuthorizationEventData,
  ChangeLanguageEventData,
  InitializeEventData,
  PaymentPanelOrigin,
  PaymentPanelV2EventName
} from '../types/payment-panel-events';
import { ComposableCssVariablesMap } from '../types/css-variable-key-list';
/**
 * Sends a message to the payment panel
 */
export const sendMessageToPayment = <T>(eventName: PaymentPanelV2EventName, eventData: T): void => {
  logger.info('Sending event to payment panel:', eventName, eventData);

  window.postMessage(
    {
      eventName,
      eventData,
      origin: PaymentPanelOrigin.Checkout // TODO: update to use Components
    },
    window.location.origin
  );
};

/**
 * Initializes the payment panel with configuration
 */
export const initialize = (config: PaymentComponentConfig): void => {
  const baseUrl = environment.themeBaseUrl;
  const paymentPanelHash = CSS_VERSIONS.paymentPanel;
  const cardPanelHash = CSS_VERSIONS.cardPanel;

  const paymentPanelThemeUrl = `${baseUrl}/payment-panel.${paymentPanelHash}.css`;
  const cardPanelThemeUrl = `${baseUrl}/card-panel.${cardPanelHash}.css`;

  const initEventData: InitializeEventData = {
    authorization_confirmation: false,
    fullRedirectFlowDisabled: true,
    isMFE: true,
    style: {
      paymentPanelThemeUrl,
      cardPanelThemeUrl,
      cssVariablesList: stylingVariablesList(config.styling),
      paymentPanelLayout: 'vertical'
    },
    culture: config.language ?? 'en-US',
    i18n: {
      localized_text: config.i18n ?? {}
    },
    paymentId: config.paymentId,
    isCheckoutComposable: true
  };

  sendMessageToPayment(PaymentPanelV2EventName.Initialize, initEventData);
};

/**
 * Sends language update to the payment panel
 */
export const updateLanguage = (config: Pick<PaymentComponentConfig, 'language' | 'i18n'>): void => {
  const changeLanguageEventData: ChangeLanguageEventData = {
    culture: config.language ?? 'en-US',
    i18n: {
      localized_text: config.i18n ?? {}
    }
  };

  sendMessageToPayment(PaymentPanelV2EventName.ChangeLanguage, changeLanguageEventData);
};

export const stylingVariablesList = (styling?: PaymentComponentConfig['styling']): ComposableCssVariablesMap => {
  return getCssVariablesList(styling);
};

/**
 * Sends clear subscriptions command to payment panel
 */
export const clearAllSubscriptions = (): void => {
  sendMessageToPayment(PaymentPanelV2EventName.ClearAllSubscriptions, {});
};

/**
 * Maps internal payment panel events to public output events
 */
export const mapPaymentEvent = (eventName: PaymentPanelV2EventName, eventData: unknown): PaymentOutputEvent | null => {
  if (eventName === PaymentPanelV2EventName.PaymentMethodChanged) {
    return 'payment_method_changed';
  }

  if (eventName === PaymentPanelV2EventName.Authorization) {
    const authData = eventData as AuthorizationEventData;
    return authData?.status === 'success' ? 'succeeded' : 'payment_failed';
  }

  return null;
};

/**
 * Hides skeleton loader when initialized with valid documentHeight
 */
const handleInitializedEvent = (eventData: unknown): void => {
  const documentHeight = (eventData as { documentHeight?: number })?.documentHeight;

  if (!documentHeight || documentHeight <= 0) {
    return;
  }

  hideSkeleton();
};

/**
 * Creates a message handler for internal payment panel events
 */
export const createMessageHandler = (onEvent: (eventName: string, data: unknown) => void): ((event: MessageEvent) => void) => {
  return (event: MessageEvent): void => {
    if (event.data?.origin !== PaymentPanelOrigin.Payment) {
      return;
    }

    if (!event.data) {
      return;
    }

    logger.info('Received message from payment component:', event.data);

    // Handle initialized event internally - hide skeleton when documentHeight > 0
    if (event.data.eventName === PaymentPanelV2EventName.Initialized) {
      handleInitializedEvent(event.data.eventData);
      return;
    }

    const trackedEvents = [PaymentPanelV2EventName.PaymentMethodChanged, PaymentPanelV2EventName.Authorization];

    if (trackedEvents.includes(event.data.eventName)) {
      logger.info(`Event ${event.data.eventName} triggered:`, event.data);

      const outputEvent = mapPaymentEvent(event.data.eventName, event.data.eventData);

      if (outputEvent) {
        logger.info(`Triggering output event: ${outputEvent}`, event.data.eventData);
        onEvent(outputEvent, event.data.eventData);
      } else {
        logger.error(`No output event mapping found for ${event.data.eventName}`);
      }
    }
  };
};
